import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const { loginWithGoogleSession } = useAuth();
  const navigate = useNavigate();
  const handled = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { setError("Missing session"); return; }
    const sessionId = decodeURIComponent(m[1]);

    (async () => {
      const res = await loginWithGoogleSession(sessionId);
      // Always strip the fragment so refreshes don't re-trigger.
      // replaceState fails silently in some sandboxed iframes — safe to ignore.
      try { window.history.replaceState(null, "", window.location.pathname); } catch { /* sandboxed iframe — fragment will simply persist */ }
      if (!res.ok) { setError(res.error || "Google sign-in failed"); return; }
      const role = res.user.role;
      if (role === "vendor") navigate("/dashboard/vendor", { replace: true });
      else if (role === "admin") navigate("/dashboard/admin", { replace: true });
      else navigate("/", { replace: true });
    })();
  }, [loginWithGoogleSession, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-sm text-center">
          <AlertCircle className="h-10 w-10 text-red-600 mx-auto mb-4" />
          <h1 className="font-display text-xl font-medium mb-2">Sign-in failed</h1>
          <p className="text-sm text-neutral-500 mb-5">{error}</p>
          <Button onClick={() => navigate("/login", { replace: true })} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="auth-callback-back">
            Back to login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-10 w-10 text-blue-600 animate-spin mx-auto mb-4" />
        <p className="text-sm text-neutral-500">Completing Google sign-in…</p>
      </div>
    </div>
  );
}
